import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const productsRef = useRef<HTMLDivElement>(null);
  const productsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const productCategories = [
    { name: 'Steel Buildings', path: '/products/steel-building', icon: 'ri-building-2-line' },
    { name: 'Windows & Doors', path: '/products/windows-doors', icon: 'ri-door-open-line' },
    { name: 'Kitchens', path: '/products/kitchens', icon: 'ri-restaurant-line' },
    { name: 'Flooring', path: '/products/flooring', icon: 'ri-layout-grid-line' },
    { name: 'Lighting', path: '/products/lighting', icon: 'ri-lightbulb-line' },
    { name: 'Plumbing', path: '/products/plumbing', icon: 'ri-drop-line' },
    { name: 'Furniture', path: '/products/furniture', icon: 'ri-sofa-line' },
    { name: 'Hardware', path: '/products/hardware', icon: 'ri-tools-line' },
    { name: 'Exteriors', path: '/products/exteriors', icon: 'ri-home-4-line' },
  ];

  const navLinks = [
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Blog', path: '/blog' },
    { name: 'Investors', path: '/investors' },
    { name: 'Contact', path: '/contact' },
  ];

  const handleProductsEnter = () => {
    if (productsTimeoutRef.current) {
      clearTimeout(productsTimeoutRef.current);
      productsTimeoutRef.current = null;
    }
    setIsProductsOpen(true);
  };

  const handleProductsLeave = () => {
    productsTimeoutRef.current = setTimeout(() => {
      setIsProductsOpen(false);
    }, 150);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-60 h-14 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/935aab48ca7cc9be920114780efacabb/51c22907bfdcf60642ffb7cb7948ccf5.png" 
                  alt="Go Global Inc. Logo" 
                  className="h-full w-auto object-contain"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {/* Products Dropdown */}
              <div
                ref={productsRef}
                className="relative"
                onMouseEnter={handleProductsEnter}
                onMouseLeave={handleProductsLeave}
              >
                <button
                  className={`text-sm font-medium transition-colors hover:text-primary-600 flex items-center gap-1 cursor-pointer ${
                    isScrolled ? 'text-gray-700' : 'text-white'
                  }`}
                >
                  Products
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isProductsOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isProductsOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3">
                    <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-3 w-56 overflow-hidden">
                      {productCategories.map((cat) => (
                        <Link
                          key={cat.path}
                          to={cat.path}
                          className="flex items-center gap-3 px-5 py-2.5 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                        >
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className={`${cat.icon} text-base`}></i>
                          </span>
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-primary-600 ${
                    isScrolled ? 'text-gray-700' : 'text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <button
                onClick={() => setShowQuoteModal(true)}
                className="bg-primary-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Quote
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-1">
              {/* Mobile Products Accordion */}
              <div>
                <button
                  onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                  className="w-full flex items-center justify-between text-gray-700 hover:text-primary-600 font-medium py-2 cursor-pointer"
                >
                  Products
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isMobileProductsOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isMobileProductsOpen && (
                  <div className="pl-4 pb-2 space-y-1">
                    {productCategories.map((cat) => (
                      <Link
                        key={cat.path}
                        to={cat.path}
                        className="flex items-center gap-3 text-gray-600 hover:text-primary-600 font-medium py-2 text-sm cursor-pointer"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-base`}></i>
                        </span>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block text-gray-700 hover:text-primary-600 font-medium py-2"
                >
                  {link.name}
                </Link>
              ))}
              <button
                onClick={() => {
                  setShowQuoteModal(true);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap mt-2"
              >
                Request Quote
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Quote Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Get Your Project Quote</h3>
              <button
                onClick={() => setShowQuoteModal(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-6">Fill out the form below and we'll provide you with a detailed quote showing how much you can save with direct factory pricing.</p>
              <QuoteForm onSuccess={() => setShowQuoteModal(false)} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function QuoteForm({ onSuccess }: { onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    materials: '',
    timeline: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6f4ee2ff40lgbk6e90g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          materials: '',
          timeline: '',
          message: ''
        });
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setStatus('error');
        setErrorMessage('Failed to submit. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Network error. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <form id="project-quote-request" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-1">
            Project Type *
          </label>
          <select
            id="projectType"
            name="projectType"
            required
            value={formData.projectType}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="">Select project type</option>
            <option value="Custom Home">Custom Home</option>
            <option value="Multi-Family Development">Multi-Family Development</option>
            <option value="Commercial Building">Commercial Building</option>
            <option value="Renovation">Renovation</option>
            <option value="Steel Building">Steel Building</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-1">
            Project Timeline *
          </label>
          <select
            id="timeline"
            name="timeline"
            required
            value={formData.timeline}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="">Select timeline</option>
            <option value="Immediate (0-3 months)">Immediate (0-3 months)</option>
            <option value="Short-term (3-6 months)">Short-term (3-6 months)</option>
            <option value="Mid-term (6-12 months)">Mid-term (6-12 months)</option>
            <option value="Long-term (12+ months)">Long-term (12+ months)</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="materials" className="block text-sm font-medium text-gray-700 mb-1">
          Materials Needed *
        </label>
        <input
          type="text"
          id="materials"
          name="materials"
          required
          placeholder="e.g., Steel building, windows, flooring, kitchens"
          value={formData.materials}
          onChange={handleChange}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
        />
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
          Project Details
        </label>
        <textarea
          id="message"
          name="message"
          rows={4}
          maxLength={500}
          placeholder="Tell us about your project, budget, and any specific requirements..."
          value={formData.message}
          onChange={handleChange}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
        ></textarea>
        <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
      </div>

      {status === 'success' && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
          ✓ Quote request submitted successfully! We'll contact you within 24 hours.
        </div>
      )}

      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {status === 'submitting' ? 'Submitting...' : 'Get My Quote'}
      </button>
    </form>
  );
}